import type {
  PendingUser,
  PersonsListResponse,
  RelationshipsListResponse,
} from "@family/shared";

type Person = PersonsListResponse["persons"][number];
type Relationship = RelationshipsListResponse["relationships"][number];

export interface TreeSuggestion {
  person: Person;
  /** Why this person is offered, e.g. "child of Yonas Kidane". */
  reason: string;
  /** True when the person descends from the branch the applicant named. */
  inBranch: boolean;
  score: number;
}

function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .trim();
}

function tokens(s: string | null | undefined): string[] {
  return norm(s ?? "")
    .split(/\s+/)
    .filter((t) => t.length >= 2);
}

function nameTokens(p: Person): string[] {
  return tokens([p.givenName, p.familyName, p.nickname].filter(Boolean).join(" "));
}

/** Count of query tokens that match a name token (prefix either way, so
 *  "Yonas" hits "Yonas" and "Tesfa" hits "Tesfay"). */
function overlap(query: string[], name: string[]): number {
  let n = 0;
  for (const q of query) {
    if (name.some((t) => t === q || t.startsWith(q) || q.startsWith(t))) n++;
  }
  return n;
}

export function personLabel(p: Person): string {
  const name = [p.givenName, p.familyName].filter(Boolean).join(" ") || "Unnamed";
  return p.birthDate ? `${name} (${p.birthDate.slice(0, 4)})` : name;
}

/**
 * Client-side matcher for a pending sign-up against the whole tree. Works
 * from what the applicant told us — their own name, their parents' names,
 * the branch they picked — and explains each hit so a moderator can judge
 * it at a glance. Deterministic and cheap: a few hundred persons.
 */
export function suggestTreeMatches(
  user: PendingUser,
  persons: Person[],
  rels: Relationship[],
): TreeSuggestion[] {
  const byId = new Map(persons.map((p) => [p.id, p]));
  const parentToChildren = new Map<string, string[]>();
  for (const r of rels) {
    if (r.kind !== "parent") continue; // fromPerson = child, toPerson = parent
    const arr = parentToChildren.get(r.toPerson);
    if (arr) arr.push(r.fromPerson);
    else parentToChildren.set(r.toPerson, [r.fromPerson]);
  }

  // Everyone under the named branch, so branch members can be ranked first.
  const branch = new Set<string>();
  if (user.branchPersonId && byId.has(user.branchPersonId)) {
    const queue = [user.branchPersonId];
    while (queue.length > 0) {
      const id = queue.pop()!;
      if (branch.has(id)) continue;
      branch.add(id);
      queue.push(...(parentToChildren.get(id) ?? []));
    }
  }

  const out = new Map<string, TreeSuggestion>();
  const add = (p: Person, reason: string, score: number) => {
    const inBranch = branch.has(p.id);
    const total = score + (inBranch ? 5 : 0);
    const prev = out.get(p.id);
    if (prev) {
      prev.score = Math.max(prev.score, total);
      if (!prev.reason.includes(reason)) prev.reason += ` · ${reason}`;
      return;
    }
    out.set(p.id, { person: p, reason, inBranch, score: total });
  };

  const own = tokens(user.displayName);
  if (own.length > 0) {
    const need = Math.min(2, own.length);
    for (const p of persons) {
      const n = overlap(own, nameTokens(p));
      if (n >= need) add(p, "name match", 3 * n);
    }
  }

  const parents: Array<[string, string | null]> = [
    ["father", user.fatherName],
    ["mother", user.motherName],
  ];
  for (const [label, name] of parents) {
    const t = tokens(name);
    if (t.length === 0) continue;
    const need = Math.min(2, t.length);
    for (const p of persons) {
      const n = overlap(t, nameTokens(p));
      if (n < need) continue;
      add(p, `matches ${label}'s name`, 2 * n);
      for (const childId of parentToChildren.get(p.id) ?? []) {
        const child = byId.get(childId);
        if (child) add(child, `child of ${personLabel(p)}`, 4 + n);
      }
    }
  }

  const rel = tokens(user.knownRelative);
  if (rel.length > 0) {
    const need = Math.min(2, rel.length);
    for (const p of persons) {
      if (overlap(rel, nameTokens(p)) >= need) add(p, "named as a relative", 1);
    }
  }

  return [...out.values()]
    .sort((a, b) => b.score - a.score || personLabel(a.person).localeCompare(personLabel(b.person)))
    .slice(0, 6);
}
